import { FC, useMemo } from 'react';
import { Blue100, Button } from '@traceair/tds';
import { PlusIcon } from '@traceair/webapp-icons';
import { ItemRow } from '../../components/ItemRow';
import { GcpMaterialRow } from '../../components/GcpMaterialRow';
import { TimeMaterialsDateHeader, TimeMaterialsCategoryHeader } from './TimeMaterialsDateGroup';
import {
  groupBySiteSummary,
  buildConsultantGcpRateInfo,
  isGcpRateFullySet,
  getGcpItemAmount,
  buildFlightsSubtitle,
  buildGcpSubtitle,
} from '../../InvoicingUtils';
import { ScanTimeMaterialsDateGroup, TimeMaterialsDatePrices } from '../../hooks/useTimeMaterialsDateGroups';
import { GcpMaterialsHandlers } from '../../types';

type TimeMaterialsCostBlockProps = {
  timeMaterialsDateGroups: ScanTimeMaterialsDateGroup[];
  expanded: Record<string, boolean>;
  timeMaterialsDatePrices: Record<string, TimeMaterialsDatePrices>;
  setDatePrice: (dateKey: string, field: keyof TimeMaterialsDatePrices, val: string) => void;
  gcpItemPrices: Record<string, string>;
  updateGcpItemPrice: (id: string, val: string) => void;
  gcpDefaultAmountById: Map<string, number>;
  gcpMaterials: GcpMaterialsHandlers;
  toggleSection: (id: string) => void;
};

export const TimeMaterialsCostBlock: FC<TimeMaterialsCostBlockProps> = ({
  timeMaterialsDateGroups,
  expanded,
  timeMaterialsDatePrices,
  setDatePrice,
  gcpItemPrices,
  updateGcpItemPrice,
  gcpDefaultAmountById,
  gcpMaterials,
  toggleSection,
}) => {
  // Keyed by dateKey so the sum of a date's GCP Materials/Mileage entries is computed once per
  // render instead of being re-derived for every date group on every render.
  const gcpMaterialsTotalsByDate = useMemo(() => {
    const totals = new Map<string, number>();
    for (const group of timeMaterialsDateGroups) {
      const total = group.gcpItems
        .map((item) => item.gcpServiceId ?? item.id)
        .flatMap((key) => gcpMaterials.entries[key] ?? [])
        .reduce((s, e) => s + parseFloat(e.amount || '0'), 0);
      totals.set(group.dateKey, total);
    }
    return totals;
  }, [timeMaterialsDateGroups, gcpMaterials.entries]);

  return (
    <>
      {timeMaterialsDateGroups.map((group) => {
        const isDateExpanded = expanded[group.dateKey] !== false;
        const isFlightsExpanded = expanded[`${group.dateKey}-flights`] === true;
        const isGcpExpanded = expanded[`${group.dateKey}-gcp`] === true;
        const prices = timeMaterialsDatePrices[group.dateKey];

        const gcpMaterialsTotalForDate = gcpMaterialsTotalsByDate.get(group.dateKey) ?? 0;

        const dateTotal =
          parseFloat(prices?.flightsCosts || '0') +
          gcpMaterialsTotalForDate +
          group.gcpItemsTotal +
          parseFloat(prices?.mileage || '0');

        return (
          <TimeMaterialsDateHeader
            key={group.dateKey}
            displayDate={group.displayDate}
            total={dateTotal}
            isExpanded={isDateExpanded}
            onToggle={() => toggleSection(group.dateKey)}
          >
            {group.scanPanoLidarItems.length > 0 && (
              <TimeMaterialsCategoryHeader
                title="Scans, panoramas price"
                subtitle={buildFlightsSubtitle(group.scanPanoLidarItems) || undefined}
                isExpanded={isFlightsExpanded}
                onToggle={() => toggleSection(`${group.dateKey}-flights`)}
                price={prices?.flightsCosts ?? ''}
                onPriceChange={(val) => setDatePrice(group.dateKey, 'flightsCosts', val)}
              >
                {groupBySiteSummary(group.scanPanoLidarItems).map((merged, idx, arr) => (
                  <ItemRow
                    key={idx}
                    hideDate
                    isLast={idx === arr.length - 1}
                    item={{
                      id: String(idx),
                      date: '',
                      siteName: merged.project,
                      customer: merged.account,
                      status: merged.isFailed ? 'failed' : 'billable',
                      rateInfo: merged.summary,
                    }}
                  />
                ))}
              </TimeMaterialsCategoryHeader>
            )}

            {group.gcpItems.length > 0 && (
              <TimeMaterialsCategoryHeader
                title="GCP services"
                subtitle={buildGcpSubtitle(group.gcpItems)}
                isExpanded={isGcpExpanded}
                onToggle={() => toggleSection(`${group.dateKey}-gcp`)}
                amount={group.gcpItemsTotal + gcpMaterialsTotalForDate}
              >
                {group.gcpItems.map((item, idx, arr) => {
                  const gcpServiceId = item.gcpServiceId ?? item.id;
                  const entries = gcpMaterials.entries[gcpServiceId] ?? [];
                  const hasMaterials = entries.some((e) => e.type === 'materials');
                  // Once the consultant's hourly rate and confirmed hours are both known, the
                  // price is fully auto-calculated and shown read-only; otherwise it's editable.
                  const isRateFullySet = isGcpRateFullySet(
                    item.consultantGcpServiceHourlyRate,
                    item.consultantGcpServiceTimeInHours,
                  );
                  return (
                    <div key={item.id}>
                      <ItemRow
                        hideDate
                        isLast={idx === arr.length - 1}
                        item={{
                          id: item.id,
                          date: '',
                          siteName: item.project,
                          customer: item.account,
                          status: item.isFailed ? 'failed' : 'billable',
                          category: item.category,
                          itemCount: item.itemCount,
                          rateInfo: item.isFailed
                            ? undefined
                            : buildConsultantGcpRateInfo(
                                item.consultantGcpServiceHourlyRate,
                                item.consultantGcpServiceTimeInHours,
                              ),
                          amount:
                            !item.isFailed && isRateFullySet
                              ? getGcpItemAmount(
                                  item.cost,
                                  item.consultantGcpServiceHourlyRate,
                                  item.consultantGcpServiceTimeInHours,
                                )
                              : undefined,
                        }}
                        amountInput={
                          item.isFailed || isRateFullySet
                            ? undefined
                            : gcpItemPrices[item.id] ?? String(gcpDefaultAmountById.get(item.id) ?? 0)
                        }
                        onAmountChange={(val) => updateGcpItemPrice(item.id, val)}
                        actionNode={
                          !item.isFailed && !hasMaterials ? (
                            <Button
                              size="xs"
                              text="GCP Materials"
                              style="secondary"
                              icon={<PlusIcon size={14} color={Blue100} />}
                              onClick={() => gcpMaterials.onAdd(gcpServiceId, 'materials')}
                            />
                          ) : undefined
                        }
                      />
                      {entries.map((entry) => (
                        <GcpMaterialRow
                          key={entry.id}
                          timeMaterials
                          label={entry.type === 'materials' ? 'GCP Materials' : 'Mileage'}
                          amount={entry.amount}
                          onAmountChange={(val) => gcpMaterials.onAmountChange(gcpServiceId, entry.id, val)}
                          onRemove={() => gcpMaterials.onRemove(gcpServiceId, entry.id)}
                        />
                      ))}
                    </div>
                  );
                })}
              </TimeMaterialsCategoryHeader>
            )}

            <TimeMaterialsCategoryHeader
              title="Mileage"
              price={prices?.mileage ?? ''}
              onPriceChange={(val) => setDatePrice(group.dateKey, 'mileage', val)}
            />
          </TimeMaterialsDateHeader>
        );
      })}
    </>
  );
};
